// Le registre des types de champs, et les seules fonctions qui le lisent.
//
// Le reste du code ne connaît pas les types : il passe un `FieldDef` à ces
// fonctions. Ajouter un type à `FIELD_TYPES` sans son module échoue ici, à la
// compilation.

import { z } from "zod";
import type { FieldKind } from "./kind";
import { checkboxKind } from "./kinds/checkbox";
import { multiSelectKind, selectKind, statusKind } from "./kinds/choice";
import { dateKind } from "./kinds/date";
import { emailKind } from "./kinds/email";
import { fileKind } from "./kinds/file";
import { nodeKind } from "./kinds/node";
import { relationKind } from "./kinds/relation";
import { numberKind } from "./kinds/number";
import { phoneKind } from "./kinds/phone";
import { richTextKind } from "./kinds/richText";
import { createdTimeKind, updatedTimeKind } from "./kinds/systemTime";
import { textKind } from "./kinds/text";
import { urlKind } from "./kinds/url";
import {
  FIELD_TYPES,
  type CoerceCtx,
  type CoerceResult,
  type FieldDef,
  type FieldSettings,
  type FieldType,
  type FieldValue,
  type FormatCtx,
  type RowMeta,
} from "./types";

export const FIELD_KINDS: { [T in FieldType]: FieldKind<T> } = {
  text: textKind,
  rich_text: richTextKind,
  number: numberKind,
  checkbox: checkboxKind,
  select: selectKind,
  multi_select: multiSelectKind,
  status: statusKind,
  date: dateKind,
  url: urlKind,
  email: emailKind,
  phone: phoneKind,
  file: fileKind,
  node: nodeKind,
  relation: relationKind,
  created_time: createdTimeKind,
  updated_time: updatedTimeKind,
};

/**
 * Le module d'un champ, vu sans son type précis. C'est le seul endroit où
 * l'on renonce au typage par type : `FieldDef` garantit que `settings` et les
 * valeurs correspondent au `type`.
 */
export function kindOf(type: FieldType): FieldKind<FieldType> {
  return FIELD_KINDS[type] as unknown as FieldKind<FieldType>;
}

export function isFieldType(value: unknown): value is FieldType {
  return typeof value === "string" && (FIELD_TYPES as readonly string[]).includes(value);
}

// ── Définitions de champs ─────────────────────────────────────────────────

const FIELD_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

const baseFieldSchema = z.object({
  id: z.string().regex(FIELD_ID_RE),
  name: z.string().trim().min(1).max(100),
  description: z.string().max(2000).optional(),
  required: z.boolean().optional(),
});

const fieldDefSchemas = FIELD_TYPES.map((type) =>
  baseFieldSchema.extend({
    type: z.literal(type),
    settings: (kindOf(type).settings as z.ZodType).prefault({}),
    default: (kindOf(type).value as z.ZodType).optional(),
  }),
);

/**
 * Valide une définition de champ, réglages compris (valeurs par défaut
 * remplies, clés inconnues retirées). Le défaut doit être une valeur du
 * type : un défaut invalide est refusé, pas ignoré.
 */
export const fieldDefSchema = z
  .discriminatedUnion(
    "type",
    fieldDefSchemas as unknown as [
      (typeof fieldDefSchemas)[number],
      ...(typeof fieldDefSchemas)[number][],
    ],
  )
  .refine(
    (field) => !(kindOf(field.type).readOnly && field.default !== undefined),
    "A computed field has no default value.",
  ) as unknown as z.ZodType<FieldDef>;

/** Une liste de champs : ids uniques, noms uniques. */
export const fieldListSchema = z
  .array(fieldDefSchema)
  .max(200)
  .refine(
    (fields) => new Set(fields.map((field) => field.id)).size === fields.length,
    "Field ids must be unique.",
  )
  .refine(
    (fields) =>
      new Set(fields.map((field) => field.name.trim().toLowerCase())).size === fields.length,
    "Field names must be unique.",
  );

export function defaultSettings<T extends FieldType>(type: T): FieldSettings<T> {
  return FIELD_KINDS[type].settings.parse({}) as FieldSettings<T>;
}

// ── Valeurs ───────────────────────────────────────────────────────────────

function isBlank(input: unknown): boolean {
  return (
    input === null ||
    input === undefined ||
    (typeof input === "string" && input.trim() === "") ||
    (Array.isArray(input) && input.length === 0)
  );
}

/**
 * Entrée brute → valeur stockée (ou `null` pour vide). C'est la porte de
 * toutes les écritures : UI, agent, MCP, CSV, workflows, conversions.
 */
export function coerceFieldValue<T extends FieldType>(
  field: FieldDef<T>,
  input: unknown,
  ctx: CoerceCtx,
): CoerceResult<T> {
  const kind = kindOf(field.type);
  if (kind.readOnly) {
    return { ok: false, reason: "This field is computed and cannot be written." };
  }
  if (isBlank(input)) return { ok: true, value: null };
  const result = kind.coerce(input, field.settings, ctx);
  if (!result.ok || result.value === null) return result as CoerceResult<T>;
  if (kind.isEmpty?.(result.value)) return { ok: true, value: null };
  // Filet : un module qui produirait une forme invalide est un bug, pas une
  // donnée à stocker.
  const checked = kind.value.safeParse(result.value);
  if (!checked.success) {
    return { ok: false, reason: `Invalid value: ${checked.error.issues[0]?.message ?? ""}` };
  }
  return { ok: true, value: checked.data as FieldValue<T> };
}

/**
 * Une valeur stockée relue : validée, `null` si vide ou invalide (une donnée
 * corrompue s'affiche vide plutôt que de casser la vue). Les champs calculés
 * se lisent sur `meta`.
 */
export function readFieldValue<T extends FieldType>(
  field: FieldDef<T>,
  stored: unknown,
  meta?: RowMeta,
): FieldValue<T> | null {
  const kind = kindOf(field.type);
  if (kind.compute) return meta ? (kind.compute(meta) as FieldValue<T> | null) : null;
  if (stored === null || stored === undefined) return null;
  const parsed = kind.value.safeParse(stored);
  if (!parsed.success) return null;
  if (kind.isEmpty?.(parsed.data)) return null;
  return parsed.data as FieldValue<T>;
}

export function formatFieldValue<T extends FieldType>(
  field: FieldDef<T>,
  value: FieldValue<T> | null,
  ctx: FormatCtx,
): string {
  if (value === null) return "";
  return kindOf(field.type).format(value, field.settings, ctx);
}
